import { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '@utils/analytics';

const AUTO_CLOSE_MS = 1500;

export interface UseCopyEmojiOptions {
  /**
   * Called after a successful clipboard write (e.g. to record popularity).
   * Read through a ref, so an inline arrow is fine — it does not destabilize
   * the returned `copy` reference.
   */
  onCopied?: (emoji: string) => void;
}

export interface UseCopyEmojiResult {
  /**
   * Copy `emoji` to the clipboard and show the "Copied!" popover anchored to
   * `event.currentTarget`. Accepts any synthetic event (mouse or keyboard) —
   * only `currentTarget` is used.
   */
  copy: (emoji: string, event: React.SyntheticEvent<HTMLElement>) => Promise<void>;
  /** Anchor element for the popover (null when closed). */
  anchorEl: HTMLElement | null;
  /** Whether the "Copied!" popover is open. */
  open: boolean;
  /** Close the popover immediately (also clears the auto-close timer). */
  close: () => void;
}

/**
 * Shared copy-emoji interaction: clipboard write, `emoji_copy` analytics,
 * popover anchor/open state, and the auto-close timer.
 *
 * The timer lives in a ref and is cleared BOTH on rapid re-copy (the popover
 * simply moves and the countdown restarts) and on unmount (no setState after
 * unmount, no stray timer). Used by HomePage (EmojiCard grid, PopularEmojis)
 * and ClickableEmojiTile so the copy flow cannot drift again.
 */
export function useCopyEmoji(options: UseCopyEmojiOptions = {}): UseCopyEmojiResult {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the latest callback without making `copy` depend on it. Assigned in
  // an effect (not during render) for concurrent-rendering purity; deps keep
  // the "latest callback" semantics.
  const onCopiedRef = useRef(options.onCopied);
  useEffect(() => {
    onCopiedRef.current = options.onCopied;
  }, [options.onCopied]);

  // Mount state for the async continuation in `copy` (bail out after
  // unmount: no analytics, no onCopied, no state writes, no stray timer).
  // StrictMode-safe pattern: TRUE is (re)set in the effect BODY — React 18
  // dev-mode double-invokes mount effects (mount → cleanup → mount), so a
  // ref initialized true with a cleanup-only false would stay permanently
  // false and silently disable every copy under `yarn start`.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Clear any pending auto-close timer on unmount.
  useEffect(() => clearTimer, [clearTimer]);

  const close = useCallback(() => {
    clearTimer();
    setOpen(false);
    setAnchorEl(null);
  }, [clearTimer]);

  const copy = useCallback(
    async (emoji: string, event: React.SyntheticEvent<HTMLElement>) => {
      // Capture the element reference BEFORE async operations
      const target = event.currentTarget as HTMLElement;

      try {
        await navigator.clipboard.writeText(emoji);
        // Unmounted while the clipboard write was in flight: the copy itself
        // succeeded, but nothing else may happen — no popularity recording,
        // no analytics, no state writes, no stray timer.
        if (!mountedRef.current) return;
        onCopiedRef.current?.(emoji);

        trackEvent('emoji_copy', {
          event_category: 'engagement',
          event_label: emoji,
          value: 1,
        });

        // Rapid re-copy: restart the countdown instead of letting the first
        // timer close the popover early under the second copy.
        clearTimer();
        setAnchorEl(target);
        setOpen(true);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          setOpen(false);
          setAnchorEl(null);
        }, AUTO_CLOSE_MS);
      } catch (err) {
        console.error('Failed to copy emoji:', err);
      }
    },
    [clearTimer]
  );

  return { copy, anchorEl, open, close };
}
